/**
 * Agent approval thresholds — query + mutation hooks.
 *
 * GET is a plain read; PUT is step-up-gated server-side (access_governance.manage), so the
 * mutation goes through withStepUp — configuring WHEN an agent action needs human
 * approval is a governance act. The T3 DECISION itself is never set here (it stays
 * passkey-bound to the delegating human).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ConsequencePolicy } from "@pureid/auth-core";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { agentApprovalPolicySchema, type AgentApprovalPolicy } from "./schemas";

function endpoint(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agent-approval-policy`;
}

const queryKey = (tenantID: string) => ["agents", "approval-policy", tenantID] as const;

export async function fetchAgentApprovalPolicy(tenantID: string): Promise<AgentApprovalPolicy> {
  const raw = await apiFetch<unknown>(endpoint(tenantID));
  return agentApprovalPolicySchema.parse(raw);
}

export function useAgentApprovalPolicyQuery(tenantID: string) {
  return useQuery<AgentApprovalPolicy, ApiError>({
    queryKey: queryKey(tenantID),
    queryFn: () => fetchAgentApprovalPolicy(tenantID),
    enabled: tenantID.length > 0,
    staleTime: 60_000,
  });
}

// null clears the tenant default (the constraint is removed; the per-agent registry
// ceiling alone governs). A policy object sets the generic per-dimension default.
export function useUpdateAgentApprovalPolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AgentApprovalPolicy, ApiError, ConsequencePolicy | null>({
    mutationFn: (policy) =>
      withStepUp(() =>
        apiFetch<unknown>(endpoint(tenantID), {
          method: "PUT",
          body: { default_consequence_policy: policy },
        }),
      ).then((raw) => agentApprovalPolicySchema.parse(raw)),
    onSuccess: (policy) => {
      qc.setQueryData(queryKey(tenantID), policy);
    },
  });
}

// Disable (un-opt-in): removes the tenant policy so every value-bearing agent action
// escalates to human approval again. The "turn off agent autonomy" lever.
export function useDisableAgentApprovalPolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AgentApprovalPolicy, ApiError, void>({
    mutationFn: () =>
      withStepUp(() => apiFetch<unknown>(endpoint(tenantID), { method: "DELETE" })).then((raw) =>
        agentApprovalPolicySchema.parse(raw),
      ),
    onSuccess: (policy) => {
      qc.setQueryData(queryKey(tenantID), policy);
    },
  });
}
